import Styles from './styles.module.css';
import React, { useEffect, useState } from 'react';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { Applicant, docData, defaultDocumentData, formatApplicant, formatDocument } from 'src/type';
import Resume from '../../info/resume/index';

type Props = {
  applicationId: string;
};

// 履歴書タブ。項目の羅列ではなく、既存の履歴書ビュー(src/salon/desktop/info/resume/)を
// そのまま再利用し、実際の履歴書として見える形(写真付き・罫線入りの1枚レイアウト)で表示する。
// このコンポーネント自体はモーダル/背景オーバーレイを持たない純粋な表示コンポーネントのため、
// タブの中にそのまま埋め込める(外側の info/index.tsx 側だけがモーダル用の枠を持つ)。
const ResumeTab: React.FC<Props> = ({ applicationId }) => {

  const [ applicant, setApplicant ] = useState<Applicant | null>(null);
  const [ documentData, setDocumentData ] = useState<docData>(defaultDocumentData);
  const [ loading, setLoading ] = useState(true);

  // 審査画面(16.judging)などでは履歴書が幅360pxの狭いペインに入るため字が小さい。
  // 「大きく表示する」で画面右側にせり出すパネルを開き、同じ履歴書ビューを拡大表示する。
  const [ enlarged, setEnlarged ] = useState(false);

  useEffect(() => {
    if (!applicationId) return;
    setLoading(true);
    (async () => {
      const db = getFirestore();
      const applicantSnap = await getDoc(doc(db, 'applicants', applicationId));

      if (!applicantSnap.exists()) {
        setApplicant(null);
        setLoading(false);
        return;
      }

      const formattedApplicant = formatApplicant(applicantSnap);
      setApplicant(formattedApplicant);

      if (formattedApplicant.recruitId) {
        const recruitSnap = await getDoc(doc(db, 'recruits', formattedApplicant.recruitId));
        setDocumentData(recruitSnap.exists() ? formatDocument(recruitSnap.data()) : defaultDocumentData);
      }

      setLoading(false);
    })();
  }, [applicationId]);

  // 別の応募者に切り替えたら拡大パネルは閉じる。
  useEffect(() => {
    setEnlarged(false);
  }, [applicationId]);

  // 拡大パネル表示中はEscで閉じられるようにする。
  useEffect(() => {
    if (!enlarged) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEnlarged(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enlarged]);

  if (loading) {
    return <p className={Styles.empty}>読み込み中...</p>;
  }

  if (!applicant) {
    return <p className={Styles.empty}>履歴書がまだ提出されていません。</p>;
  }

  // documentData.resume は「求人側で履歴書提出を必須にしているか」という応募設定の
  // 任意フラグで、これがfalseだと(会社が設定していないだけで)基本情報ブロック自体が
  // 丸ごと非表示になってしまう。本タブはどの応募者でも履歴書を確認できることが目的のため、
  // resumeだけは常にtrueとして扱う(コーディネート写真/追加質問/その他書類は、実際に
  // その求人で求めていて回答がある場合のみ表示したいので、フラグはそのまま活かす)。
  const resumeDocumentData: docData = { ...documentData, resume: true };

  return (
    <>
      <button
        type="button"
        className={Styles.resumeEnlargeButton}
        onClick={() => setEnlarged(true)}
      >
        大きく表示する
      </button>

      <Resume documentData={resumeDocumentData} applicant={applicant} />

      {enlarged && (
        <div className={Styles.resumeEnlargeOverlay}>
          <div
            className={Styles.resumeEnlargeBackdrop}
            onClick={() => setEnlarged(false)}
          />
          <div className={Styles.resumeEnlargePanel}>
            <div className={Styles.resumeEnlargeHeader}>
              <h4>履歴書</h4>
              <button
                type="button"
                className={Styles.resumeEnlargeClose}
                onClick={() => setEnlarged(false)}
              >
                閉じる
              </button>
            </div>
            <div className={Styles.resumeEnlargeBody}>
              <div className={Styles.resumeEnlargeScale}>
                <Resume documentData={resumeDocumentData} applicant={applicant} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResumeTab;
